import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { DoesntHaveFeatureRedirectGuard, HasFeatureRedirectGuard, NavigationGuard } from '@app/guards';
import { RoutesWithOptions } from '@app/interfaces';
import { SelfOnboardingCompleteGuard } from '@payroll/guards';
import { PayrollResolver } from '@payroll/payroll.resolver';
import { BenefitTypeResolver, DeductionTypeResolver, PayrollPendingStateResolver } from '@payroll/resolvers';
import {
    BenefitEmployeeIndexView,
    BenefitIndexView,
    BenefitShowView,
    BenefitStoreView,
    CompanyAdditionalIncomeIndexView,
    CompanyAdditionalIncomeShowView,
    DebugShowView,
    DeductionEmployeeIndexView,
    DeductionIndexView,
    DeductionShowView,
    DeductionStoreView,
    MakeVacationYtdAdjustmentForm,
    PayrollCompletedView,
    PayrollIndexView,
    PayrollLandingView,
    PayrollPayingView,
    PayrollUpdateView,
    RecordPayrollDetailsView,
    RecordPayrollHoursView,
    RecordPayrollIndexView,
    RecordPayrollShowView,
    Rl1ShowView,
    RL1UpdateView,
    RoeIndexView,
    RoeShowView,
    RoeUpdateView,
    SelfOnboardingAuthorizationDocumentsView,
    SelfOnboardingCompanyInformationView,
    SelfOnboardingSupportingDocumentsView,
    SettingsShowView,
    T4ShowView,
    T4SummaryShow,
    T4UpdateView,
    TaxAdjusterView,
    TaxFormOverview,
    TaxReturnIndex,
    TaxReturnShow,
    TaxReturnSubmit,
    VacationYtdAdjusterView,
    VacationYtdAdjustmentHistoryView,
    VacationYtdIndexView,
    WorkersCompensationIndexView,
    WorkersCompensationShowView,
    WorkersCompensationStoreView,
    YtdIndexView,
} from '@payroll/views';
import { FrameView } from '@payroll/views/frame/frame.view';
import { DentalBenefitAssignView } from './views/dental-benefit/dental-benefit-assign/dental-benefit-assign.view';
import { DentalBenefitIndexView } from './views/dental-benefit/dental-benefit-index.view';

const routes: RoutesWithOptions = [
    {
        path: '',
        canActivate: [SelfOnboardingCompleteGuard],
        children: [], // SelfOnboardingCompleteGuard always redirects. Therefore, nothing is defined for this route
        pathMatch: 'full',
    },

    {
        path: 'self-onboarding',
        children: [
            {
                path: '',
                component: PayrollLandingView,
            },
            {
                path: 'company-information',
                component: SelfOnboardingCompanyInformationView,
                canDeactivate: [NavigationGuard],
            },
            {
                path: 'authorization-documents',
                component: SelfOnboardingAuthorizationDocumentsView,
            },
            {
                path: 'supporting-documents',
                component: SelfOnboardingSupportingDocumentsView,
                canActivate: [HasFeatureRedirectGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.KYC_KYB],
                        redirectTo: ['payroll', 'self-onboarding', 'business-verification'],
                    },
                },
            },
            {
                path: 'business-verification',
                component: SelfOnboardingSupportingDocumentsView,
                canActivate: [DoesntHaveFeatureRedirectGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.KYC_KYB],
                        redirectTo: ['payroll', 'self-onboarding', 'supporting-documents'],
                    },
                },
            },
        ],
    },

    // Not in use currently
    {
        path: 'frame',
        component: FrameView,
    },
    {
        path: 'run',
        children: [
            {
                path: '',
                component: PayrollIndexView,
            },
            {
                path: ':payroll',
                children: [
                    {
                        path: '',
                        component: PayrollUpdateView,
                    },
                    {
                        path: 'paying',
                        resolve: {
                            models: PayrollResolver,
                        },
                        component: PayrollPayingView,
                    },
                ],
            },
        ],
    },
    {
        path: 'records',
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'payrolls',
            },
            {
                path: 'payrolls',
                children: [
                    {
                        path: '',
                        component: RecordPayrollIndexView,
                    },
                    {
                        path: ':payroll',
                        children: [
                            {
                                path: '',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: RecordPayrollShowView,
                            },
                            {
                                path: 'details',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: RecordPayrollDetailsView,
                            },
                            {
                                path: 'hours',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: RecordPayrollHoursView,
                            },
                            {
                                path: 'completed',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: PayrollCompletedView,
                                canDeactivate: [NavigationGuard],
                            },
                        ],
                    },
                ],
            },
            {
                path: 'tax-returns',
                canActivate: [DoesntHaveFeatureRedirectGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.yearEnd2023],
                        redirectTo: ['payroll', 'records', 'tax-forms'],
                    },
                },
                children: [
                    {
                        path: '',
                        component: TaxReturnIndex,
                    },
                    {
                        path: ':year',
                        children: [
                            {
                                path: '',
                                component: TaxReturnIndex,
                            },
                            {
                                path: ':taxReturn',
                                runGuardsAndResolvers: 'always',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                children: [
                                    {
                                        path: '',
                                        component: TaxReturnShow,
                                    },
                                    {
                                        path: 'T4',
                                        children: [
                                            {
                                                path: 'submit',
                                                component: TaxReturnSubmit,
                                            },
                                        ],
                                    },
                                    {
                                        path: 'RL1',
                                        children: [
                                            {
                                                path: 'submit',
                                                component: TaxReturnSubmit,
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: 'tax-forms',
                children: [
                    {
                        path: '',
                        component: TaxFormOverview,
                    },
                    {
                        path: ':year',
                        component: TaxFormOverview,
                    },
                    {
                        path: 't4-summary/:t4Summary',
                        resolve: {
                            models: PayrollResolver,
                        },
                        component: T4SummaryShow,
                    },
                    {
                        path: 't4-show/:t4',
                        children: [
                            {
                                path: '',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: T4ShowView,
                            },
                            {
                                path: 'edit-details',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: T4UpdateView,
                            },
                            {
                                path: 'edit-employee',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: T4UpdateView,
                            },
                        ],
                    },
                    {
                        path: 'rl1-show/:rl1',
                        children: [
                            {
                                path: '',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: Rl1ShowView,
                            },
                            {
                                path: 'edit-details',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: RL1UpdateView,
                            },
                            {
                                path: 'edit-employee',
                                resolve: {
                                    models: PayrollResolver,
                                },
                                component: RL1UpdateView,
                            },
                        ],
                    },
                ],
            },
            {
                path: 'roes',
                children: [
                    {
                        path: '',
                        component: RoeIndexView,
                    },
                    {
                        path: ':roe',
                        resolve: {
                            models: PayrollResolver,
                        },
                        component: RoeShowView,
                    },
                    {
                        path: ':roe/edit',
                        component: RoeUpdateView,
                    },
                ],
            },
            {
                path: 'ytds',
                children: [
                    {
                        path: '',
                        component: YtdIndexView,
                    },
                ],
            },
            {
                path: 'vacation-ytds',
                children: [
                    {
                        path: '',
                        component: VacationYtdIndexView,
                    },
                ],
            },
            {
                path: 'adjustments',
                children: [
                    {
                        path: '',
                        component: TaxAdjusterView,
                    },
                ],
            },
        ],
    },
    {
        path: 'benefits',
        children: [
            {
                path: '',
                component: BenefitIndexView,
                resolve: {
                    isPayrollPending: PayrollPendingStateResolver,
                },
            },
            {
                path: 'create',
                component: BenefitStoreView,
                resolve: {
                    benefitTypes: BenefitTypeResolver,
                },
            },
            {
                path: ':benefit',
                children: [
                    {
                        path: '',
                        component: BenefitShowView,
                        resolve: {
                            models: PayrollResolver,
                            isPayrollPending: PayrollPendingStateResolver,
                        },
                    },
                    {
                        path: 'employees',
                        component: BenefitEmployeeIndexView,
                        resolve: {
                            models: PayrollResolver,
                            isPayrollPending: PayrollPendingStateResolver,
                        },
                    },
                    {
                        path: 'edit',
                        component: BenefitStoreView,
                        resolve: {
                            models: PayrollResolver,
                            benefitTypes: BenefitTypeResolver,
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'deductions',
        children: [
            {
                path: '',
                component: DeductionIndexView,
                resolve: {
                    isPayrollPending: PayrollPendingStateResolver,
                },
            },
            {
                path: 'create',
                component: DeductionStoreView,
                resolve: {
                    deductionTypes: DeductionTypeResolver,
                },
            },
            {
                path: ':deduction',
                children: [
                    {
                        path: '',
                        component: DeductionShowView,
                        resolve: {
                            models: PayrollResolver,
                            isPayrollPending: PayrollPendingStateResolver,
                        },
                    },
                    {
                        path: 'employees',
                        component: DeductionEmployeeIndexView,
                        resolve: {
                            models: PayrollResolver,
                            isPayrollPending: PayrollPendingStateResolver,
                        },
                    },
                    {
                        path: 'edit',
                        component: DeductionStoreView,
                        resolve: {
                            models: PayrollResolver,
                            deductionTypes: DeductionTypeResolver,
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'dental-benefits',
        children: [
            {
                path: '',
                component: DentalBenefitIndexView,
                canActivate: [DoesntHaveFeatureRedirectGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.bulkUpdateBox45],
                        redirectTo: ['payroll'],
                    },
                },
            },
            {
                path: ':benefit-code',
                component: DentalBenefitAssignView,
                canActivate: [DoesntHaveFeatureRedirectGuard],
                data: {
                    feature: {
                        flags: [FeatureFlag.bulkUpdateBox45],
                        redirectTo: ['payroll'],
                    },
                },
            },
        ],
    },
    {
        path: 'additional-incomes',
        children: [
            {
                path: '',
                component: CompanyAdditionalIncomeIndexView,
            },
            {
                path: ':companyAdditionalIncome',
                children: [
                    {
                        path: '',
                        component: CompanyAdditionalIncomeShowView,
                        resolve: {
                            models: PayrollResolver,
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'vacation-ytd-adjuster',
        children: [
            {
                path: '',
                component: VacationYtdAdjusterView,
            },
            {
                path: 'make-adjustment/:employee',
                component: MakeVacationYtdAdjustmentForm,
            },
            {
                path: 'adjustment-history/:employee',
                component: VacationYtdAdjustmentHistoryView,
            },
        ],
    },
    {
        path: 'workers-compensations',
        children: [
            {
                path: '',
                component: WorkersCompensationIndexView,
            },
            {
                path: 'create',
                component: WorkersCompensationStoreView,
            },
            {
                path: ':workersCompensation',
                component: WorkersCompensationShowView,
                resolve: {
                    models: PayrollResolver,
                    isPayrollPending: PayrollPendingStateResolver,
                },
            },
        ],
    },
    {
        path: 'settings',
        component: SettingsShowView,
    },
    {
        path: 'debug',
        component: DebugShowView,
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class PayrollRouterModule {}
